'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Loader2, CheckCircle } from 'lucide-react';

type Status = 'idle' | 'submitting' | 'approved' | 'pending' | 'exists' | 'error';

const inputCls =
  'w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-charcoal';

export default function TradeApplyForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<{ tier?: string; discount?: number } | null>(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    company_name: '',
    business_type: '',
    annual_volume: '',
    website: '',
    company_website: '', // honeypot — never shown
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setStatus('submitting');
    try {
      const res = await fetch('/api/v2/users/trade/apply-public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setStatus('exists');
        return;
      }
      if (!res.ok) {
        setError(json?.error || json?.message || 'Something went wrong. Please try again or call us.');
        setStatus('error');
        return;
      }
      const data = json?.data || json;
      setResult({ tier: data?.tier, discount: data?.discount });
      setStatus(data?.status === 'approved' ? 'approved' : 'pending');
    } catch {
      setError('Network error. Please try again.');
      setStatus('error');
    }
  }

  if (status === 'approved') {
    return (
      <div className="text-center py-6">
        <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
        <h3 className="text-xl font-bold mb-2">You're approved.</h3>
        <p className="text-gray-600 text-sm mb-5">
          <b>{result?.tier || 'Trade Pro'}</b> pricing — <b>{result?.discount ?? 10}% off</b> — is live on your account.
          Sign in and the discount applies automatically in your cart. We've emailed your details.
        </p>
        <Link href="/login?redirect=/products" className="bg-primary-red hover:bg-hover-primary text-white font-semibold px-6 py-3 rounded-lg inline-block">
          Sign in &amp; start ordering
        </Link>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="text-center py-6">
        <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
        <h3 className="text-xl font-bold mb-2">Application received.</h3>
        <p className="text-gray-600 text-sm">We'll confirm your trade pricing by email shortly.</p>
      </div>
    );
  }

  if (status === 'exists') {
    return (
      <div className="text-center py-6">
        <h3 className="text-xl font-bold mb-2">You already have an account</h3>
        <p className="text-gray-600 text-sm mb-5">Sign in and apply from <b>My Account → Trade Program</b> — approval is still instant.</p>
        <Link href="/login?redirect=/account/trade" className="bg-charcoal text-white font-semibold px-6 py-3 rounded-lg inline-block">
          Sign in to continue
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <input required maxLength={100} placeholder="First name" className={inputCls} value={form.first_name} onChange={set('first_name')} autoComplete="given-name" />
        <input required maxLength={100} placeholder="Last name" className={inputCls} value={form.last_name} onChange={set('last_name')} autoComplete="family-name" />
      </div>
      <input required type="email" maxLength={255} placeholder="Work email" className={inputCls} value={form.email} onChange={set('email')} autoComplete="email" />
      <div className="grid grid-cols-2 gap-3">
        <input required type="tel" minLength={10} maxLength={20} placeholder="Phone" className={inputCls} value={form.phone} onChange={set('phone')} autoComplete="tel" />
        <input required type="password" minLength={8} placeholder="Create a password" className={inputCls} value={form.password} onChange={set('password')} autoComplete="new-password" />
      </div>
      <input required minLength={2} maxLength={255} placeholder="Company name" className={inputCls} value={form.company_name} onChange={set('company_name')} autoComplete="organization" />
      <div className="grid grid-cols-2 gap-3">
        <select required className={inputCls} value={form.business_type} onChange={set('business_type')}>
          <option value="">I am a…</option>
          <option value="interior_designer">Interior designer</option>
          <option value="contractor">Contractor / remodeler</option>
          <option value="builder">Home builder</option>
          <option value="property_management">Property manager</option>
          <option value="window_treatment">Window-treatment dealer</option>
          <option value="installer">Installer</option>
          <option value="architect">Architect</option>
          <option value="other">Other trade</option>
        </select>
        <select className={inputCls} value={form.annual_volume} onChange={set('annual_volume')}>
          <option value="">Est. yearly volume</option>
          <option value="under_5k">Under $5,000</option>
          <option value="5k_25k">$5,000 – $25,000</option>
          <option value="25k_100k">$25,000 – $100,000</option>
          <option value="100k_plus">$100,000+</option>
        </select>
      </div>
      <input maxLength={255} placeholder="Website or Instagram (optional)" className={inputCls} value={form.website} onChange={set('website')} />
      {/* Honeypot: hidden from humans, bots fill it. Name intentionally looks real. */}
      <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
        <label>Company website<input tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set('company_website')} /></label>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-primary-red hover:bg-hover-primary disabled:opacity-60 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
      >
        {status === 'submitting' ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating your account…</> : 'Get instant trade pricing'}
      </button>
      <p className="text-xs text-gray-500 text-center">
        Password needs 8+ characters with an uppercase letter, a lowercase letter and a number.
        By applying you agree to our <Link href="/terms" className="underline">terms</Link>.
      </p>
    </form>
  );
}
